import "./style.css";
import "@fontsource/barlow-condensed/latin-600.css";
import "@fontsource/barlow-condensed/latin-700.css";
import "@fontsource/barlow-condensed/latin-800.css";
import "@fontsource/barlow-condensed/latin-800-italic.css";
import "@fontsource/ibm-plex-mono/latin-500.css";
import "@fontsource/ibm-plex-mono/latin-600.css";
import { initializeGoogleAnalytics } from "./analytics/GoogleAnalytics";
import type { Gender, MatchConfig, RosterPlayer, Team } from "./game/Types";
import type { ControlMessage } from "./networking/Protocol";
import { prepareIceConfiguration, type IceConfiguration } from "./networking/IceConfig";
import type { ConnectionMode } from "./networking/createSignalingTransport";
import { SignalingClient, type JoinRequestInfo, type LobbyInfo } from "./networking/SignalingClient";
import type { PlayerLocation } from "./networking/SignalingTransport";
import { WebRTCManager, type PeerStatus } from "./networking/WebRTCManager";
import { registerOfflineSupport } from "./offline/OfflineSupport";
import { ensureOnlineSession } from "./security/OnlineSession";
import { appTemplate } from "./ui/Templates";
import { bindTweaks } from "./ui/Tweaks";
import { bindFullscreen } from "./ui/Fullscreen";
import { JoinRequestPanel } from "./ui/JoinRequestPanel";
import { LobbyBrowser } from "./ui/LobbyBrowser";
import { bindMapPicker, setMapPickerDisabled, syncMapPicker } from "./ui/MapPicker";
import { genderLabel, PlayerIdentity } from "./ui/PlayerIdentity";
import { requestNearbyLocation } from "./ui/NearbyRooms";

class MiniDoomApp {
  private readonly app = document.querySelector<HTMLDivElement>("#app")!;
  private readonly peerId = loadPeerId();
  private readonly identity = new PlayerIdentity();
  private signaling: SignalingClient | null = null;
  private signalingMode: ConnectionMode | null = null;
  private network: WebRTCManager | null = null;
  private iceConfiguration: IceConfiguration = { servers: [], turnConfigured: false };
  private lobby: LobbyInfo | null = null;
  private peerReady = new Map<string, boolean>();
  private readonly peerStatuses = new Map<string, PeerStatus>();
  private readonly knownHostIds = new Set<string>();
  private readonly joinRequestPanel = new JoinRequestPanel(
    (requestId, accepted) => this.answerJoinRequest(requestId, accepted),
    (message) => setText("room-status", message),
  );
  private readonly lobbyBrowser = new LobbyBrowser((code) => void this.joinRoom(code));
  private joinPending = false;
  private launching = false;
  private playerLocation: PlayerLocation | undefined;
  private readonly analyticsId = loadAnalyticsId();

  start(): void {
    this.app.innerHTML = appTemplate();
    this.identity.restore();
    this.bindLanding();
    this.bindRoom();
    bindTweaks();
    bindFullscreen();
    registerOfflineSupport();
    this.bindConnectivity();
    if (navigator.onLine) this.showOnlineIdleState();
    else this.showOfflineState();
    void this.enableNearbyRooms();
  }

  private bindLanding(): void {
    this.bindIdentity();
    byId("practice").addEventListener("click", () => this.startPractice());
    byId("create-room").addEventListener("click", () => void this.createRoom());
    byId("join-room").addEventListener("click", () => void this.joinRoom(readInput("join-code")));
    byId("refresh-lobbies").addEventListener("click", () => void this.refreshLobbies());
    byId("nearby-rooms").addEventListener("click", () => void this.enableNearbyRooms());
    for (const input of document.querySelectorAll<HTMLInputElement>('input[name="network-mode"]')) {
      input.addEventListener("change", () => this.changeNetworkMode());
    }
    byId("join-code").addEventListener("keydown", (event) => {
      if ((event as KeyboardEvent).key === "Enter") void this.joinRoom(readInput("join-code"));
    });
  }

  private bindIdentity(): void {
    this.identity.bind(({ name, gender }) => this.signaling?.setPlayerIdentity(name, gender));
  }

  private bindRoom(): void {
    byId("leave-room").addEventListener("click", () => this.leaveRoom());
    byId("copy-code").addEventListener("click", () => {
      if (this.lobby) void navigator.clipboard.writeText(this.lobby.code);
    });
    byId("start-match").addEventListener("click", () => void this.startOnlineMatch());
    byId("retry-links").addEventListener("click", () => this.network?.retryConnections());
    bindMapPicker((mapId) => {
      setSelect("match-map", mapId);
      syncMapPicker();
    });
    syncMapPicker();
  }

  private bindConnectivity(): void {
    window.addEventListener("online", () => {
      if (!this.lobby && !this.launching && selectedNetworkMode() === "online") this.showOnlineIdleState();
    });
    window.addEventListener("offline", () => {
      if (!this.lobby && !this.launching && selectedNetworkMode() === "online") this.showOfflineState();
    });
  }

  private async ensureSignaling(mode: ConnectionMode): Promise<void> {
    const { name, gender } = this.identity.save();
    await ensureOnlineSession();
    if (this.signaling && this.signalingMode !== mode) {
      this.signaling.disconnect();
      this.signaling = null;
    }
    if (!this.signaling) {
      this.signaling = new SignalingClient(this.peerId, name, gender, mode);
      this.signalingMode = mode;
      this.bindSignaling(this.signaling);
    }
    this.signaling.setPlayerLocation(this.playerLocation);
    this.signaling.setAnalyticsId(this.analyticsId);
    this.signaling.setPlayerIdentity(name, gender);
    await this.signaling.connect();
    this.setNetworkStatus(mode === "lan" ? "LAN signaling ready" : "Signaling online", true);
  }

  private bindSignaling(signaling: SignalingClient): void {
    signaling.addEventListener("lobby-updated", (event) => this.updateLobby((event as CustomEvent<LobbyInfo>).detail));
    signaling.addEventListener("peer-joined", (event) => {
      const peerId = (event as CustomEvent<{ peerId: string }>).detail.peerId;
      this.network?.connectToPeers([peerId]);
    });
    signaling.addEventListener("join-request", (event) => this.joinRequestPanel.add((event as CustomEvent<JoinRequestInfo>).detail));
    signaling.addEventListener("join-request-cancelled", (event) => {
      const requestId = (event as CustomEvent<{ requestId: string }>).detail.requestId;
      this.joinRequestPanel.remove(requestId);
    });
    signaling.addEventListener("lobby-list-updated", (event) => {
      if (this.lobby) return;
      if (this.playerLocation) {
        void signaling.listLobbies().then((lobbies) => this.lobbyBrowser.render(lobbies), () => {});
      } else {
        this.lobbyBrowser.render((event as CustomEvent<LobbyInfo[]>).detail);
      }
    });
    signaling.addEventListener("offline", () => {
      if (!this.launching) this.setNetworkStatus("Signaling offline", false);
    });
    signaling.addEventListener("lobby-closed", (event) => {
      const detail = (event as CustomEvent<{ reason?: string }>).detail;
      if (!this.lobby) return;
      this.leaveRoom();
      this.setLandingMessage(detail?.reason ?? "The room host closed the room.");
    });
  }

  private async createRoom(): Promise<void> {
    try {
      this.clearError();
      const mode = selectedNetworkMode();
      await this.ensureSignaling(mode);
      const lobby = await this.signaling!.createLobby({
        name: `${this.identity.read().name}'s doom room`,
        mode: selectValue("match-mode") as "deathmatch" | "teams",
        mapId: selectValue("match-map"),
        maxPlayers: Number(selectValue("max-players")),
      });
      this.iceConfiguration = await prepareNetworkIce(mode, this.signaling!.iceSessionToken);
      this.enterLobby(lobby);
    } catch (error) {
      this.showError(error);
    }
  }

  private async joinRoom(code: string): Promise<void> {
    if (!code.trim()) return this.showError(new Error("Enter a five-character room code."));
    if (this.joinPending) return;
    try {
      this.clearError();
      this.joinPending = true;
      this.setJoinButtonsDisabled(true);
      const mode = selectedNetworkMode();
      await this.ensureSignaling(mode);
      this.setLandingMessage("Join request sent. Waiting for the room host…");
      const lobby = await this.signaling!.requestJoin(code);
      this.setLandingMessage("Host approved. Opening peer links…");
      this.iceConfiguration = await prepareNetworkIce(mode, this.signaling!.iceSessionToken);
      this.enterLobby(lobby);
    } catch (error) {
      this.showError(error);
    } finally {
      this.joinPending = false;
      this.setJoinButtonsDisabled(false);
      if (!this.lobby) this.setLandingMessage("");
    }
  }

  private async refreshLobbies(): Promise<void> {
    if (!navigator.onLine && selectedNetworkMode() === "online") return this.showOfflineState();
    this.lobbyBrowser.showLoading();
    try {
      await this.ensureSignaling(selectedNetworkMode());
      const lobbies = await this.signaling!.listLobbies();
      this.lobbyBrowser.render(lobbies);
    } catch (error) {
      this.lobbyBrowser.showUnavailable();
      this.showError(error);
    }
  }

  private enterLobby(lobby: LobbyInfo): void {
    this.lobby = lobby;
    this.peerReady = new Map(lobby.peers.map((peer) => [peer.peerId, peer.peerId === this.peerId]));
    this.network = new WebRTCManager(this.peerId, this.signaling!, this.iceConfiguration);
    this.network.addEventListener("status", (event) => this.updatePeerStatus((event as CustomEvent<PeerStatus>).detail));
    this.network.addEventListener("control", (event) => this.acceptLobbyControl((event as CustomEvent<{ peerId: string; message: ControlMessage }>).detail));
    this.network.connectToPeers(lobby.peers.map((peer) => peer.peerId));
    byId("landing").classList.add("hidden");
    byId("room").classList.remove("hidden");
    this.updateLobby(lobby);
  }

  private leaveRoom(): void {
    this.network?.close();
    this.signaling?.disconnect();
    this.network = null;
    this.signaling = null;
    this.signalingMode = null;
    this.lobby = null;
    this.peerReady.clear();
    this.peerStatuses.clear();
    this.knownHostIds.clear();
    this.joinRequestPanel.clear();
    for (const id of ["match-mode", "match-map", "max-players"]) (byId(id) as HTMLSelectElement).disabled = false;
    setMapPickerDisabled(false);
    syncMapPicker();
    byId("room").classList.add("hidden");
    byId("landing").classList.remove("hidden");
    this.setNetworkStatus("P2P ready", true);
    this.clearError();
    this.setLandingMessage("");
  }

  private changeNetworkMode(): void {
    this.signaling?.disconnect();
    this.signaling = null;
    this.signalingMode = null;
    this.clearError();
    this.setLandingMessage("");
    setText("frequency-label", selectedNetworkMode() === "lan" ? "LAN FREQUENCIES" : "ONLINE FREQUENCIES");
    (byId("nearby-rooms") as HTMLButtonElement).disabled = selectedNetworkMode() === "lan";
    if (navigator.onLine || selectedNetworkMode() === "lan") void this.refreshLobbies();
    else this.showOfflineState();
  }

  private async enableNearbyRooms(): Promise<void> {
    const button = byId("nearby-rooms") as HTMLButtonElement;
    button.disabled = true;
    try {
      this.playerLocation = await requestNearbyLocation();
      this.signaling?.setPlayerLocation(this.playerLocation);
      button.textContent = "Nearby on";
      await this.refreshLobbies();
    } catch (error) {
      this.showError(error);
    } finally {
      button.disabled = selectedNetworkMode() === "lan";
    }
  }

  private showOfflineState(): void {
    this.signaling?.disconnect();
    this.signaling = null;
    this.signalingMode = null;
    this.lobbyBrowser.showUnavailable();
    this.setNetworkStatus("Offline · practice ready", false);
    this.setLandingMessage("Practice vs bots is ready offline.");
  }

  private showOnlineIdleState(): void {
    this.lobbyBrowser.showIdle();
    this.setNetworkStatus("Online · rooms on demand", true);
    this.setLandingMessage("");
  }

  private updateLobby(lobby: LobbyInfo): void {
    if (!this.lobby || this.launching) return;
    this.lobby = lobby;
    this.knownHostIds.add(lobby.hostPeerId);
    this.network?.syncPeers(lobby.peers.map((peer) => peer.peerId));
    setText("room-name", lobby.name);
    setText("room-code", lobby.code);
    const isHost = lobby.hostPeerId === this.peerId;
    this.joinRequestPanel.setHost(isHost);
    if (!isHost) {
      setSelect("match-mode", lobby.mode);
      setSelect("match-map", lobby.mapId);
    }
    for (const id of ["match-mode", "match-map", "max-players"]) (byId(id) as HTMLSelectElement).disabled = !isHost;
    setMapPickerDisabled(!isHost);
    syncMapPicker();
    byId("room-peers").innerHTML = lobby.peers.map((peer) => {
      const ready = this.peerReady.get(peer.peerId) ?? peer.peerId === this.peerId;
      const status = this.peerStatuses.get(peer.peerId);
      const failed = status?.phase === "failed";
      const role = peerStatusLabel(peer.peerId, lobby, ready, status, this.peerId);
      return `<div class="peer-row ${ready ? "ready" : ""} ${failed ? "failed" : ""}"><span class="peer-dot"></span><b>${escapeHtml(peer.name)}<small>${genderLabel(peer.gender)}</small></b><span>${role}</span></div>`;
    }).join("");
    this.updateStartButton();
  }

  private answerJoinRequest(requestId: string, accepted: boolean): Promise<void> {
    return this.signaling?.respondToJoin(requestId, accepted)
      ?? Promise.reject(new Error("The signaling connection is not ready."));
  }

  private updatePeerStatus(status: PeerStatus): void {
    this.peerReady.set(status.peerId, status.ready);
    this.peerStatuses.set(status.peerId, status);
    if (this.lobby && !this.launching) this.updateLobby(this.lobby);
  }

  private updateStartButton(): void {
    if (!this.lobby || !this.network) return;
    const isHost = this.lobby.hostPeerId === this.peerId;
    const enoughPlayers = this.lobby.peers.length >= 2;
    const linksReady = this.network.allReady(this.lobby.peers.length - 1);
    const statuses = [...this.peerStatuses.values()];
    const failed = statuses.find((status) => status.phase === "failed");
    const retrying = statuses.find((status) => status.phase === "retrying");
    const start = byId("start-match") as HTMLButtonElement;
    byId("retry-links").classList.toggle("hidden", !failed);
    start.disabled = !isHost || !enoughPlayers || !linksReady;
    setText("room-status", failed
      ? failed.reason ?? "The direct peer route failed."
      : retrying ? `Retrying ICE path (${retrying.restartAttempts}/2)…`
        : !isHost ? "Waiting for the host to launch."
      : !enoughPlayers ? "Invite at least one rival."
        : !linksReady ? "Opening encrypted peer channels…" : "All peer links locked. Ready to launch.");
  }

  private async startOnlineMatch(): Promise<void> {
    if (!this.lobby || !this.network || this.lobby.hostPeerId !== this.peerId) return;
    if (!this.network.allReady(this.lobby.peers.length - 1)) return;
    const mode = selectValue("match-mode") as "deathmatch" | "teams";
    const roster = makeRoster(this.lobby, mode);
    const config: MatchConfig = {
      mode,
      mapId: selectValue("match-map"),
      scoreLimit: 10,
      seed: crypto.getRandomValues(new Uint32Array(1))[0]!,
    };
    try {
      await this.signaling?.startLobby();
    } catch (error) {
      setText("room-status", error instanceof Error ? error.message : "Could not lock the room.");
      return;
    }
    this.network.broadcastControl({ type: "match-start", config, roster, hostId: this.peerId });
    this.launchMatch(config, roster, this.peerId, []);
  }

  private acceptLobbyControl(detail: { peerId: string; message: ControlMessage }): void {
    if (detail.message.type !== "match-start"
      || detail.peerId !== detail.message.hostId
      || !this.knownHostIds.has(detail.peerId)) return;
    this.launchMatch(detail.message.config, detail.message.roster, detail.message.hostId, []);
  }

  private startPractice(): void {
    const identity = this.identity.save();
    const names = [identity.name, "Vex", "Nyx", "Brass"];
    const genders: Gender[] = [identity.gender, "male", "female", "male"];
    const roster: RosterPlayer[] = names.map((name, order) => ({ id: order === 0 ? this.peerId : `bot-${order}`, name, gender: genders[order]!, team: "none", order }));
    const config: MatchConfig = { mode: "practice", mapId: "foundry", scoreLimit: 10, seed: 0xc0ffee };
    this.launchMatch(config, roster, this.peerId, roster.slice(1).map((peer) => peer.id));
  }

  private launchMatch(config: MatchConfig, roster: RosterPlayer[], hostId: string, botIds: string[]): void {
    if (this.launching) return;
    this.launching = true;
    this.identity.save();
    this.signaling?.disconnect();
    void import("./game/GameLauncher").then(({ launchGame }) => launchGame({
      app: this.app,
      config,
      roster,
      localPeerId: this.peerId,
      network: this.network,
      hostId,
      botIds,
    }));
  }

  private showError(error: unknown): void {
    setText("landing-error", error instanceof Error ? error.message : "Something interrupted the link.");
  }

  private clearError(): void {
    setText("landing-error", "");
  }

  private setLandingMessage(message: string): void {
    setText("landing-message", message);
  }

  private setJoinButtonsDisabled(disabled: boolean): void {
    for (const button of document.querySelectorAll<HTMLButtonElement>("[data-lobby-code], #join-room")) button.disabled = disabled;
  }

  private setNetworkStatus(label: string, online: boolean): void {
    setText("network-label", label);
    document.querySelector(".network-pill")?.classList.toggle("offline", !online);
  }
}

const makeRoster = (lobby: LobbyInfo, mode: "deathmatch" | "teams"): RosterPlayer[] => lobby.peers
  .sort((a, b) => a.order - b.order)
  .map((peer, index) => ({ ...peer, id: peer.peerId, team: (mode === "teams" ? (index % 2 === 0 ? "ember" : "azure") : "none") as Team }));

const peerStatusLabel = (
  peerId: string,
  lobby: LobbyInfo,
  ready: boolean,
  status: PeerStatus | undefined,
  localPeerId: string,
): string => {
  if (peerId === localPeerId) return peerId === lobby.hostPeerId ? "HOST" : "YOU";
  if (ready) return "LINKED";
  if (status?.phase === "failed") return "ICE FAILED";
  if (status?.phase === "retrying") return `RETRY ${status.restartAttempts}/2`;
  return status?.iceState === "checking" ? "ICE CHECK" : "LINKING";
};

const loadPeerId = (): string => {
  const stored = sessionStorage.getItem("rift-runners-peer-id");
  if (stored) return stored;
  const id = createPeerId();
  sessionStorage.setItem("rift-runners-peer-id", id);
  return id;
};

const createPeerId = (): string => {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
};

const loadAnalyticsId = (): string => {
  const key = "mini-doom-analytics-id";
  const stored = localStorage.getItem(key);
  if (stored) return stored;
  const id = createPeerId();
  localStorage.setItem(key, id);
  return id;
};

const selectedNetworkMode = (): ConnectionMode =>
  document.querySelector<HTMLInputElement>('input[name="network-mode"]:checked')?.value === "lan" ? "lan" : "online";

const prepareNetworkIce = (
  mode: ConnectionMode,
  sessionToken: string | null,
): Promise<IceConfiguration> => mode === "lan"
  ? Promise.resolve({ servers: [], turnConfigured: false })
  : prepareIceConfiguration(sessionToken);

const byId = (id: string): HTMLElement => document.getElementById(id)!;
const readInput = (id: string): string => (byId(id) as HTMLInputElement).value;
const selectValue = (id: string): string => (byId(id) as HTMLSelectElement).value;
const setSelect = (id: string, value: string): void => { (byId(id) as HTMLSelectElement).value = value; };
const setText = (id: string, value: string): void => { byId(id).textContent = value; };
const escapeHtml = (value: string): string => value.replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#039;", '"': "&quot;" })[character]!);

initializeGoogleAnalytics();
new MiniDoomApp().start();
